import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { successPackagesQueryKeys } from '../api';
import { SuccessPackageAction } from './SuccessPackageAction';
import type { SuccessPackage, PackageFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: PackageFilters;
}

function formatPaymentValue(value: number, unit: string): string {
  if (unit === 'PERCENT') return `${value}%`;
  return value?.toLocaleString() ?? '0';
}

export function useSuccessPackageColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<SuccessPackage>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<SuccessPackage>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/success-packages/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'minClaimAmount',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('minClaimAmount')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('minClaimAmount')}
        >
          <p>{t('minClaimAmount')}</p>
          <SortIcon active={orderBy === 'minClaimAmount'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.minClaimAmount?.toLocaleString()}</div>,
    },
    {
      accessorKey: 'maxClaimAmount',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('maxClaimAmount')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('maxClaimAmount')}
        >
          <p>{t('maxClaimAmount')}</p>
          <SortIcon active={orderBy === 'maxClaimAmount'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.maxClaimAmount?.toLocaleString()}</div>,
    },
    {
      accessorKey: 'advancePayment',
      header: () => (
        <div className="flex items-center">
          <p>{t('advancePayment')}</p>
        </div>
      ),
      cell: ({ row }) => (
        <div>{formatPaymentValue(row.original.advancePayment, row.original.advancePaymentUnit)}</div>
      ),
    },
    {
      accessorKey: 'successPayment',
      header: () => (
        <div className="flex items-center">
          <p>{t('successPayment')}</p>
        </div>
      ),
      cell: ({ row }) => (
        <div>{formatPaymentValue(row.original.successPayment, row.original.successPaymentUnit)}</div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        return <div>{format(new Date(createdAt), 'yyyy-MM-dd HH:mm')}</div>;
      },
    },
  ];

  if (hasAccess('success-package', 'update') || hasAccess('success-package', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className="flex gap-2">
            {hasAccess('success-package', 'update') && (
              <SuccessPackageAction id={data.id} />
            )}
            {hasAccess('success-package', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.name}
                url="success-packages"
                queryKey={successPackagesQueryKeys.lists()}
              />
            )}
          </div>
        );
      },
    });
  }

  return columns;
}
